import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Euro } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESI_LABEL = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const fmtEur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

/** Report mensile incassato / da incassare per anno (fatture). */
export const IncassiReport = () => {
  const [anno, setAnno] = useState(null);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/statistiche/incassi${anno ? `?anno=${anno}` : ''}`);
      setData(res.data);
      if (!anno) setAnno(res.data.anno);
    } catch (e) {
      console.error('Errore report incassi:', e);
    }
  }, [anno]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!data) return null;

  const chartData = data.mesi.map((m, i) => ({
    mese: MESI_LABEL[i],
    Incassato: m.incassato,
    'Da incassare': m.da_incassare,
  }));

  return (
    <Card className="rounded-md" data-testid="incassi-report">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-sm font-mono uppercase flex items-center gap-2">
            <Euro className="h-4 w-4 text-emerald-500" />
            Riepilogo Incassi {data.anno}
          </CardTitle>
          <Select value={String(data.anno)} onValueChange={(v) => setAnno(Number(v))}>
            <SelectTrigger className="w-28 rounded-md h-8 text-xs" data-testid="incassi-anno-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-md">
              {data.anni_disponibili.map((a) => (
                <SelectItem key={a} value={String(a)}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <div className="p-3 rounded-md bg-muted/50 border">
            <p className="text-[10px] font-mono uppercase text-muted-foreground">Emesso {data.anno}</p>
            <p className="text-lg font-black" data-testid="incassi-tot-emesso">{fmtEur(data.totali.emesso)}</p>
          </div>
          <div className="p-3 rounded-md bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900">
            <p className="text-[10px] font-mono uppercase text-muted-foreground">Incassato</p>
            <p className="text-lg font-black text-emerald-600" data-testid="incassi-tot-incassato">{fmtEur(data.totali.incassato)}</p>
          </div>
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900">
            <p className="text-[10px] font-mono uppercase text-muted-foreground">Da incassare</p>
            <p className="text-lg font-black text-red-600" data-testid="incassi-tot-da-incassare">{fmtEur(data.totali.da_incassare)}</p>
            <p className="text-[10px] text-muted-foreground">{data.totali.n_da_incassare} fatture{data.totali.scadute > 0 ? ` · ${data.totali.scadute} scadute` : ''}</p>
          </div>
          <div className="p-3 rounded-md bg-muted/50 border">
            <p className="text-[10px] font-mono uppercase text-muted-foreground">% Incassato</p>
            <p className="text-lg font-black text-cyan-600" data-testid="incassi-percentuale">
              {data.totali.emesso > 0 ? Math.round((data.totali.incassato / data.totali.emesso) * 100) : 0}%
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.3} />
            <XAxis dataKey="mese" stroke="#666" fontSize={11} />
            <YAxis stroke="#666" fontSize={11} tickFormatter={(v) => `€${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
            <Tooltip formatter={(value, name) => [fmtEur(value), name]} contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 6 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Incassato" fill="#10b981" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Da incassare" fill="#ef4444" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>

        <div className="overflow-x-auto mt-4">
          <table className="w-full text-xs" data-testid="incassi-tabella">
            <thead>
              <tr className="border-b text-muted-foreground font-mono uppercase text-[10px]">
                <th className="text-left py-1.5 pr-2">Mese</th>
                <th className="text-right py-1.5 px-2">Emesso</th>
                <th className="text-right py-1.5 px-2">Incassato</th>
                <th className="text-right py-1.5 px-2">Da incassare</th>
                <th className="text-right py-1.5 pl-2">Fatture</th>
              </tr>
            </thead>
            <tbody>
              {data.mesi.map((m, i) => (
                (m.emesso > 0 || m.incassato > 0) && (
                  <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                    <td className="py-1.5 pr-2 font-medium">{MESI_LABEL[i]}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{fmtEur(m.emesso)}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-emerald-600">{fmtEur(m.incassato)}</td>
                    <td className={`py-1.5 px-2 text-right font-mono ${m.da_incassare > 0 ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>{fmtEur(m.da_incassare)}</td>
                    <td className="py-1.5 pl-2 text-right text-muted-foreground">{m.n_emesse} em. / {m.n_pagate} pag.</td>
                  </tr>
                )
              ))}
            </tbody>
            <tfoot>
              <tr className="font-bold">
                <td className="py-2 pr-2">TOTALE</td>
                <td className="py-2 px-2 text-right font-mono">{fmtEur(data.totali.emesso)}</td>
                <td className="py-2 px-2 text-right font-mono text-emerald-600">{fmtEur(data.totali.incassato)}</td>
                <td className="py-2 px-2 text-right font-mono text-red-600">{fmtEur(data.totali.da_incassare)}</td>
                <td className="py-2 pl-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
